import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import {
  faBookmark,
  faPlusLarge,
  faTrash,
} from '@fortawesome/pro-solid-svg-icons';
import classNames from 'classnames';
import isEqual from 'lodash.isequal';
import type { ChangeEvent, FormEvent } from 'react';
import { Fragment, useCallback, useMemo, useState } from 'react';
import { useInstantSearch } from 'react-instantsearch';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { Dropdown } from '@sorare/core/src/atoms/dropdowns/Dropdown';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { ChevronDownBold } from '@sorare/core/src/atoms/icons/ChevronDownBold';
import { SearchInput } from '@sorare/core/src/atoms/inputs/SearchInput';
import { BodyM } from '@sorare/core/src/atoms/typography';
import { Dialog } from '@sorare/core/src/components/dialog';
import type { ExtendedUIState } from '@sorare/core/src/components/search/InstantSearch/types';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useIsTabletAndAbove } from '@sorare/core/src/hooks/device/useIsTabletAndAbove';
import { useScreenSize } from '@sorare/core/src/hooks/device/useScreenSize';
import { useMutation } from '@sorare/core/src/hooks/graphql/useMutation';
import { useGetInteractionContext } from '@sorare/core/src/hooks/useGetInteractionContext';
import { useToggle } from '@sorare/core/src/hooks/useToggle';
import { useVirtualToggleManager } from '@sorare/core/src/hooks/useVirtualToggleManager';
import { useEvents } from '@sorare/core/src/lib/events/useEvents';
import { VIRTUAL_TOGGLE_FILTERS } from '@sorare/core/src/lib/filters';
import { glossary } from '@sorare/core/src/lib/glossary';

import { useCurrentMarketplaceUserContext } from '#src/contexts/currentMarketplaceUser/index.ts';
import { useFiltersCount } from '#src/search/FiltersManager/useFiltersCount.ts';

import type {
  UpdateSavedFiltersMutation,
  UpdateSavedFiltersMutationVariables,
} from './__generated__/index.graphql.ts';

type CardFilters = {
  query?: string;
  refinementList?: Record<string, string[]>;
  range?: Record<string, string>;
  toggle?: Record<string, boolean>;
  virtualToggle?: Record<string, string | boolean>;
  menu?: Record<string, string>;
};

const UPDATE_SAVED_FILTERS_MUTATION = gql`
  mutation UpdateSavedFiltersMutation(
    $input: updateCardFiltersInput!
    $sport: Sport!
  ) {
    updateCardFilters(input: $input) {
      currentUser {
        slug
        cardFilters(sport: $sport)
      }
      errors {
        code
        message
      }
    }
  }
` as TypedDocumentNode<
  UpdateSavedFiltersMutation,
  UpdateSavedFiltersMutationVariables
>;

const DropdownContent = styled.div`
  width: 240px;
`;
const Row = styled.div`
  height: 56px;
  display: flex;
  justify-content: space-between;
  align-items: stretch;

  &:hover {
    background-color: var(--c-nd-100);
  }

  & > *:first-child {
    flex: 1;
    overflow: hidden;
  }
`;
const StyledButton = styled(ButtonBase)`
  text-align: left;
  padding: 0 var(--double-unit);
  color: var(--c-nd-600);

  &.active {
    color: var(--c-blue-300);
  }
`;
const AddButton = styled(StyledButton)`
  width: 100%;
  height: 56px;
  display: inline-flex;
  align-items: center;
  gap: var(--unit);
  color: var(--c-white);

  &:not(:disabled):hover {
    background-color: var(--c-nd-100);
  }

  &:disabled {
    color: var(--c-nd-200);
    cursor: initial;
  }
`;
const StyledText16 = styled(BodyM)`
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
`;
const DeleteButton = styled(StyledButton)`
  width: 46px;
  color: var(--c-nd-400);

  &:hover {
    color: var(--c-white);
  }
`;
const DialogForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: var(--double-unit);
  margin-bottom: 0;
  padding: 0 var(--triple-unit) var(--triple-unit) var(--triple-unit);
`;

const FilterRow = ({
  label,
  onClick,
  onDelete,
  active,
}: {
  label: string;
  onClick: () => void;
  onDelete: () => void;
  active: boolean;
}) => {
  const [hovering, setHovering] = useState(false);
  const { up: isLaptop } = useScreenSize('laptop');

  const showDeleteBtn = !isLaptop || hovering;
  return (
    <Row
      onMouseOver={() => setHovering(true)}
      onMouseLeave={() => setHovering(false)}
    >
      <StyledButton
        type="button"
        onClick={onClick}
        className={classNames({ active })}
      >
        <StyledText16 title={label} bold>
          {label}
        </StyledText16>
      </StyledButton>
      {!!showDeleteBtn && (
        <DeleteButton type="button" onClick={onDelete}>
          <FontAwesomeIcon icon={faTrash} />
        </DeleteButton>
      )}
    </Row>
  );
};

export const SavedFilters = () => {
  const { sport } = useSportContext();
  const { currentUser } = useCurrentUserContext();
  const track = useEvents();
  const { indexUiState, setIndexUiState } = useInstantSearch<ExtendedUIState>();
  const filtersCount = useFiltersCount();
  const { up: isLaptop } = useScreenSize('laptop');
  const setVirtualToggleFilters = useVirtualToggleManager();
  const [open, toggleOpen] = useToggle(false);
  const [name, setName] = useState<string>('');
  const getInteractionContext = useGetInteractionContext();
  const isTabletAndAbove = useIsTabletAndAbove();

  const currentUiState: CardFilters = useMemo(() => {
    const uiStateWithVirtualToggle: CardFilters = indexUiState;

    return {
      ...(uiStateWithVirtualToggle.query
        ? { query: uiStateWithVirtualToggle.query }
        : {}),
      toggle: uiStateWithVirtualToggle.toggle ?? {},
      refinementList: uiStateWithVirtualToggle.refinementList ?? {},
      range: uiStateWithVirtualToggle.range ?? {},
      virtualToggle: uiStateWithVirtualToggle.virtualToggle ?? {},
      menu: uiStateWithVirtualToggle.menu ?? {},
    };
  }, [indexUiState]);

  const { cardFilters } = useCurrentMarketplaceUserContext();
  const [updateFilters] = useMutation(UPDATE_SAVED_FILTERS_MUTATION);

  const activeFiltersKey = useMemo(() => {
    return Object.entries(cardFilters || {}).find(([, filters]) =>
      isEqual(currentUiState, filters)
    )?.[0];
  }, [currentUiState, cardFilters]);

  const toTrackingFilters = (filters: CardFilters): Record<string, string> => {
    const flattenFilters = {
      ...(filters.query ? { query: filters.query } : {}),
      ...Object.values({
        refinementList: filters.refinementList,
        range: filters.range,
        toggle: filters.toggle,
        virtualToggle: filters.virtualToggle,
        menu: filters.menu,
      }).reduce<Record<string, string | string[] | boolean>>(
        (acc, value) => ({ ...acc, ...value }),
        {}
      ),
    };

    return Object.fromEntries(
      Object.entries(flattenFilters).map(([filterName, value]) => {
        const matchingVirtualToggle = Object.values(
          VIRTUAL_TOGGLE_FILTERS
        ).find(
          ({ name: virtualToggleName }) => virtualToggleName === filterName
        );

        return [
          matchingVirtualToggle?.trackingName || filterName,
          value.toString(),
        ];
      })
    );
  };

  const onSubmit = useCallback(
    (event: FormEvent) => {
      if (!sport) return;

      updateFilters({
        variables: {
          input: {
            sport,
            filters: {
              [name]: currentUiState,
            },
          },
          sport,
        },
      });

      track('Create Saved Search', {
        interactionContext: getInteractionContext(),
        sport,
        name,
        filters: toTrackingFilters(currentUiState),
      });

      toggleOpen();
      setName('');
      event.preventDefault();
    },
    [
      currentUiState,
      sport,
      name,
      toggleOpen,
      updateFilters,
      track,
      getInteractionContext,
    ]
  );

  const applyFilters = useCallback(
    (filters: CardFilters) => {
      setVirtualToggleFilters(
        {
          ...Object.fromEntries(
            Object.values(VIRTUAL_TOGGLE_FILTERS).map(filter => [
              filter.name,
              filter.defaultValue,
            ])
          ),
          ...filters.virtualToggle,
        },
        false
      );
      setIndexUiState(filters);

      track('Select Saved Search', {
        name,
        filters: toTrackingFilters(filters),
      });
    },
    [setIndexUiState, setVirtualToggleFilters, track, name]
  );

  const deleteFilters = useCallback(
    (key: string, filters: CardFilters) => {
      if (!sport) return;

      updateFilters({
        variables: {
          input: {
            sport,
            filters: {
              [key]: null,
            },
          },
          sport,
        },
      });

      track('Delete Saved Search', {
        name: key,
        filters: toTrackingFilters(filters),
      });
    },
    [sport, updateFilters, track]
  );

  if (!sport || !currentUser) return null;

  const DropdownContainer = !isLaptop ? Fragment : DropdownContent;

  return (
    <>
      <Dropdown
        align="right"
        label={props => (
          <Button
            {...props}
            disableDebounce
            color={activeFiltersKey ? 'primary' : 'tertiary'}
            startIcon={<FontAwesomeIcon icon={faBookmark} />}
            size="small"
            fullWidth={isTabletAndAbove}
          >
            {!!isTabletAndAbove && (
              <FormattedMessage {...glossary.mySearches} />
            )}
            <ChevronDownBold />
          </Button>
        )}
        gap={16}
      >
        {({ closeDropdown }) => (
          <DropdownContainer>
            <AddButton
              type="button"
              disabled={!!activeFiltersKey || !filtersCount}
              onClick={() => {
                closeDropdown();
                toggleOpen();
              }}
            >
              <FontAwesomeIcon icon={faPlusLarge} size="xs" />
              <StyledText16 bold>
                <FormattedMessage
                  id="Search.SavedFilters.addToSavedSearches"
                  defaultMessage="Add to Saved searches"
                />
              </StyledText16>
            </AddButton>
            {Object.entries(cardFilters).map(([key, filters]) => (
              <FilterRow
                key={key}
                label={key}
                onClick={() => {
                  closeDropdown();
                  applyFilters(filters);
                }}
                onDelete={() => deleteFilters(key, filters)}
                active={activeFiltersKey === key}
              />
            ))}
          </DropdownContainer>
        )}
      </Dropdown>
      <Dialog
        maxWidth="xs"
        fullWidth
        open={open}
        onClose={toggleOpen}
        title={
          <BodyM className="text-center">
            <FormattedMessage
              id="Search.SavedFilters.nameYourSavedSearch"
              defaultMessage="Name your Saved Search"
            />
          </BodyM>
        }
      >
        <DialogForm onSubmit={onSubmit}>
          <SearchInput
            autoFocus
            value={name}
            onChange={(event: ChangeEvent<HTMLInputElement>) => {
              setName(event.target.value);
            }}
            rounded
            small
          />
          <Button color="primary" size="small" type="submit">
            <FormattedMessage {...glossary.save} />
          </Button>
        </DialogForm>
      </Dialog>
    </>
  );
};
